"use client";

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuthContextStore } from "@/features/auth/services/auth-context-store";
import {
  archiveNotification,
  getNotificationsUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/features/notifications/services/notifications-api";
import {
  getNextNotificationsPageParam,
  mapNotificationListCache,
  markNotificationsReadInPage,
  type NotificationListCache,
  removeNotificationFromPage,
  replaceNotificationInPage,
} from "@/features/notifications/lib/notification-inbox-pages";
import {
  type ListNotificationsQueryDto,
  type ListNotificationsResponseDto,
  type NotificationDto,
  type NotificationsUnreadCountResponseDto,
  NotificationStatus,
} from "@/features/notifications/model/notifications.types";

const NOTIFICATIONS_ROOT_KEY = ["notifications"] as const;
const NOTIFICATIONS_POLL_INTERVAL_MS = 45_000;

function getNotificationsWorkspaceKey(workspaceSlug: string | null) {
  return workspaceSlug ?? "__all__";
}

function getNotificationsUserKey(userId: string | null | undefined) {
  return userId ?? "__user__";
}

function buildNotificationsListQueryKey({
  workspaceSlug,
  userId,
  page,
  limit,
  unreadOnly,
  scope,
}: {
  workspaceSlug: string | null;
  userId: string | null | undefined;
  page: number;
  limit: number;
  unreadOnly: boolean;
  scope: "dropdown" | "page";
}) {
  return [
    ...NOTIFICATIONS_ROOT_KEY,
    "list",
    scope,
    getNotificationsUserKey(userId),
    getNotificationsWorkspaceKey(workspaceSlug),
    unreadOnly ? "unread" : "all",
    page,
    limit,
  ] as const;
}

/** Scrollable bell panel: one key for all pages (no page segment). Stays under `list` so the cache updaters match it. */
function buildNotificationsInfiniteListQueryKey({
  workspaceSlug,
  userId,
  limit,
  unreadOnly,
}: {
  workspaceSlug: string | null;
  userId: string | null | undefined;
  limit: number;
  unreadOnly: boolean;
}) {
  return [
    ...NOTIFICATIONS_ROOT_KEY,
    "list",
    "dropdown",
    getNotificationsUserKey(userId),
    getNotificationsWorkspaceKey(workspaceSlug),
    unreadOnly ? "unread" : "all",
    "infinite",
    limit,
  ] as const;
}

function buildNotificationsUnreadCountQueryKey(workspaceSlug: string | null, userId: string | null | undefined) {
  return [
    ...NOTIFICATIONS_ROOT_KEY,
    "unread-count",
    getNotificationsUserKey(userId),
    getNotificationsWorkspaceKey(workspaceSlug),
  ] as const;
}

function isNotificationsUnreadCountResponse(value: unknown): value is NotificationsUnreadCountResponseDto {
  if (!value || typeof value !== "object") return false;
  return typeof (value as NotificationsUnreadCountResponseDto).unreadCount === "number";
}

function updateNotificationListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  transform: (page: ListNotificationsResponseDto) => ListNotificationsResponseDto,
) {
  queryClient.setQueriesData<NotificationListCache>(
    {
      queryKey: NOTIFICATIONS_ROOT_KEY,
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "notifications" &&
        query.queryKey[1] === "list",
    },
    (current) => mapNotificationListCache(current, transform),
  );
}

function decrementUnreadCount(
  queryClient: ReturnType<typeof useQueryClient>,
  amount: number,
) {
  if (amount <= 0) return;

  queryClient.setQueriesData<NotificationsUnreadCountResponseDto>(
    {
      queryKey: NOTIFICATIONS_ROOT_KEY,
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "notifications" &&
        query.queryKey[1] === "unread-count",
    },
    (current) => {
      if (!isNotificationsUnreadCountResponse(current)) return current;

      return {
        unreadCount: Math.max(0, current.unreadCount - amount),
      };
    },
  );
}

function resetUnreadCounts(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.setQueriesData<NotificationsUnreadCountResponseDto>(
    {
      queryKey: NOTIFICATIONS_ROOT_KEY,
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "notifications" &&
        query.queryKey[1] === "unread-count",
    },
    (current) => {
      if (!isNotificationsUnreadCountResponse(current)) return current;
      return { unreadCount: 0 };
    },
  );
}

export function useNotificationsQuery(query: ListNotificationsQueryDto) {
  const workspaceSlug = useAuthContextStore((state) => state.activeWorkspaceSlug);
  const userId = useAuthContextStore((state) => state.authMe?.user.id);
  const page = query.page ?? 1;
  const limit = query.limit ?? 20;
  const unreadOnly = query.unreadOnly ?? false;

  return useQuery({
    queryKey: buildNotificationsListQueryKey({
      workspaceSlug,
      userId,
      page,
      limit,
      unreadOnly,
      scope: "page",
    }),
    queryFn: () => listNotifications({ page, limit, unreadOnly }, { workspaceSlug }),
    enabled: Boolean(userId),
    refetchOnWindowFocus: true,
  });
}

/**
 * Bell panel list: loads the inbox page by page as the user scrolls. The backend only returns the last
 * NOTIFICATION_INBOX_WINDOW_DAYS days, so running out of pages means "everything from that window is shown".
 */
export function useInfiniteNotificationsQuery({
  limit = 10,
  unreadOnly = false,
}: {
  limit?: number;
  unreadOnly?: boolean;
}) {
  const workspaceSlug = useAuthContextStore((state) => state.activeWorkspaceSlug);
  const userId = useAuthContextStore((state) => state.authMe?.user.id);

  return useInfiniteQuery({
    queryKey: buildNotificationsInfiniteListQueryKey({ workspaceSlug, userId, limit, unreadOnly }),
    queryFn: ({ pageParam }) => listNotifications({ page: pageParam, limit, unreadOnly }, { workspaceSlug }),
    initialPageParam: 1,
    getNextPageParam: getNextNotificationsPageParam,
    enabled: Boolean(userId),
    refetchOnWindowFocus: true,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
  });
}

export function useNotificationsUnreadCountQuery() {
  const workspaceSlug = useAuthContextStore((state) => state.activeWorkspaceSlug);
  const userId = useAuthContextStore((state) => state.authMe?.user.id);

  return useQuery({
    queryKey: buildNotificationsUnreadCountQueryKey(workspaceSlug, userId),
    queryFn: () => getNotificationsUnreadCount({ workspaceSlug }),
    enabled: Boolean(userId),
    refetchOnWindowFocus: true,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
  });
}

export function useMarkNotificationReadMutation() {
  const queryClient = useQueryClient();
  const workspaceSlug = useAuthContextStore((state) => state.activeWorkspaceSlug);

  return useMutation({
    mutationFn: ({ id }: { id: string; status?: NotificationDto["status"] }) => markNotificationRead(id, { workspaceSlug }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_ROOT_KEY });

      const readAt = new Date().toISOString();
      updateNotificationListCaches(queryClient, (page) =>
        markNotificationsReadInPage(page, (notification) => notification.id === id, readAt),
      );

      if (status === NotificationStatus.Unread) {
        decrementUnreadCount(queryClient, 1);
      }
    },
    onSuccess: (notification) => {
      updateNotificationListCaches(queryClient, (page) => replaceNotificationInPage(page, notification));
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_ROOT_KEY });
    },
  });
}

export function useMarkAllNotificationsReadMutation() {
  const queryClient = useQueryClient();
  const workspaceSlug = useAuthContextStore((state) => state.activeWorkspaceSlug);
  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId);

  return useMutation({
    mutationFn: () => markAllNotificationsRead({ workspaceId: workspaceId ?? undefined }, { workspaceSlug }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_ROOT_KEY });
      const readAt = new Date().toISOString();
      updateNotificationListCaches(queryClient, (page) => markNotificationsReadInPage(page, () => true, readAt));
      resetUnreadCounts(queryClient);
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_ROOT_KEY });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_ROOT_KEY });
    },
  });
}

export function useArchiveNotificationMutation() {
  const queryClient = useQueryClient();
  const workspaceSlug = useAuthContextStore((state) => state.activeWorkspaceSlug);

  return useMutation({
    mutationFn: ({ id }: { id: string; status?: NotificationDto["status"] }) => archiveNotification(id, { workspaceSlug }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_ROOT_KEY });
      updateNotificationListCaches(queryClient, (page) => removeNotificationFromPage(page, id));
      if (status === NotificationStatus.Unread) {
        decrementUnreadCount(queryClient, 1);
      }
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_ROOT_KEY });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_ROOT_KEY });
    },
  });
}
